/**
 * @fileoverview Firm workflow-status select (MOR-736)
 *
 * Small shared dropdown over the org's `taskStatuses` used to set the
 * workflow status on non-task entities — day-one documents, reports, and
 * data / investigation agents — so firms can manage them with the same
 * custom statuses as tasks and trigger automations off the changes.
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { cn } from "@/utils/misc";
import { TASK_COLOR_DOT } from "@/components/settings/tasks/task-colors";

const NONE = "__none__";

export function WorkflowStatusSelect({
  orgId,
  value,
  onChange,
  className,
  placeholder = "No status",
  disabled,
}: {
  orgId: Id<"organizations">;
  value: Id<"taskStatuses"> | null | undefined;
  onChange: (next: Id<"taskStatuses"> | null) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const { data: statuses } = useQuery(
    convexQuery(api.tasks.statuses.listByOrg, { orgId }),
  );

  return (
    <Select
      value={value ?? NONE}
      onValueChange={(v) =>
        onChange(v === NONE ? null : (v as Id<"taskStatuses">))
      }
      disabled={disabled}
    >
      <SelectTrigger className={cn("h-8 w-[170px] text-xs", className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE} className="text-xs">
          <span className="text-muted-foreground">{placeholder}</span>
        </SelectItem>
        {(statuses ?? []).map((s) => (
          <SelectItem key={s._id} value={s._id} className="text-xs">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  TASK_COLOR_DOT[s.color],
                )}
              />
              {s.name}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
